// #region Imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, defer, finalize, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { AppService, Response as ApiResponse } from '../app.service';
import { ListObject } from '../classes/page-content';
import { UserNewsletter } from '../classes/user';
import { AtokaAddressResult, ShippingAddressApi } from '../interfaces/shipping';
import { UserIdentity } from '../interfaces/user-identity';
import { Agent as UserAgent } from '../models/Agent';
import { BootstrapService } from './bootstrap.service';
// #endregion Imports

// #region TypesAndInterfaces
/** Codice di esito positivo nelle risposte del backend. */
const HTTP_STATUS_OK: number = 200;

/** Agente interno letto per un utente in una sessione: la lettura condivisa e la sessione a cui appartiene. */
interface InternalUserAgentCacheEntry {
  sessionKey: string;
  agent$: Observable<UserAgent>;
}
// #endregion TypesAndInterfaces

// #region ComponentDecorator
@Injectable({ providedIn: 'root' })
// #endregion ComponentDecorator
export class UserService {
  // #region PrivateFields
  // Agente interno dell'ultimo utente letto, valido per la sessione in corso: il footer di ogni pagina lo chiede
  // con lo stesso utente. Un nuovo token (login, logout, login da amministratore) vale come sessione nuova.
  private internalUserAgentCache: InternalUserAgentCacheEntry | null = null;
  // #endregion PrivateFields

  // #region DependencyInjection
  private readonly appService: AppService = inject(AppService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly bootstrap: BootstrapService = inject(BootstrapService);
  // #endregion DependencyInjection

  // #region PublicMethods
  loginFromAdmin(token: string): Observable<ApiResponse<UserIdentity>> {
    const parameters = { token };
    return this.appService.httpPostWithExternalToken<UserIdentity>(
      'Auth',
      'AdmLoginAuthorization',
      parameters,
      'Basic ' + token
    );
  }

  changeHidePrices(username: string, password: string): Observable<ApiResponse<unknown>> {
    const parameters = { username, password };
    return this.appService.httpPost<unknown>('User', 'ChangeHidePrices', parameters);
  }

  // I due salvataggi delle newsletter: il backend risponde 200 senza corpo, Data non porta nulla da leggere.
  saveNewsletter(isNewsletterSubscribed: boolean): Observable<ApiResponse<unknown>> {
    const parameters = { newsletter: isNewsletterSubscribed };
    return this.appService.httpPost<unknown>('User', 'UpdateUserNewsletter', parameters);
  }

  saveNewsletterAdvance(userNewsletter: UserNewsletter[]): Observable<ApiResponse<unknown>> {
    const parameters = { userNewsletter };
    return this.appService.httpPost<unknown>(
      'User',
      'UpdateUserNewsletterAdvance',
      parameters
    );
  }

  getShippingAddresses(): Observable<ApiResponse<ShippingAddressApi[]>> {
    return this.appService.httpGet<ShippingAddressApi[]>(
      'UserAddresses',
      `GetAllEntities/${this.appService.methodShippingAddresses}`
    );
  }

  getBillingAddress(): Observable<ApiResponse<ShippingAddressApi[]>> {
    return this.appService.httpGet<ShippingAddressApi[]>(
      'UserAddresses',
      `GetAllEntities/${this.appService.methodBillingAddress}`
    );
  }

  updateShippingAddress(address: ShippingAddressApi): Observable<ApiResponse<ShippingAddressApi>> {
    return this.appService.httpPatchJson<ShippingAddressApi>(
      'UserAddresses',
      `InsertOrUpdateUserAddress/${this.appService.methodShippingAddresses}`,
      address
    );
  }

  getAddress(id: number): Observable<ShippingAddressApi> {
    if (!id) {
      // senza identificativo non si chiama il backend: errore senza motivo, come una risposta non valida
      return throwError((): undefined => undefined);
    }

    return this.appService.httpGet<ShippingAddressApi>(
      'UserAddresses',
      `GetEntity/${id}`
    ).pipe(
      // solo gli errori della chiamata finiscono nel log; una risposta senza esito positivo fallisce in silenzio
      catchError((error: unknown): Observable<never> => {
        console.error('UserService: lettura dell\'indirizzo non riuscita', id, error);
        return throwError((): unknown => error);
      }),
      switchMap((response: ApiResponse<ShippingAddressApi> | undefined): Observable<ShippingAddressApi> =>
        response && response.Code == HTTP_STATUS_OK ? of(response.Data) : throwError((): undefined => undefined)
      )
    );
  }

  deleteAddress(id: number): Observable<ApiResponse<unknown>> {
    return this.appService.httpDelete<unknown>(
      'UserAddresses',
      `DeleteUserAddress/${id}`
    );
  }

  changeDefaultShippingAddress(id: number): Observable<ApiResponse<unknown>> {
    const parameters = { ID_Address: id };
    return this.appService.httpPost<unknown>(
      'UserAddresses',
      'ChangeDefaultShippingAddress',
      parameters
    );
  }

  saveBillingAddress(address: ShippingAddressApi): Observable<ApiResponse<ShippingAddressApi>> {
    return this.appService.httpPatchJson<ShippingAddressApi>(
      'UserAddresses',
      `InsertOrUpdateUserAddress/${this.appService.methodBillingAddress}`,
      address
    );
  }

  getAtokaAddress(vatCode: string, shouldCheckActivityCode: boolean): Observable<ApiResponse<AtokaAddressResult>> {
    const parameters = { vatCode, checkActivityCode: shouldCheckActivityCode };
    return this.appService.httpPost<AtokaAddressResult>(
      'UserAddresses',
      'GetAtokaAddress',
      parameters
    );
  }

  /** Data vale true se l'email è già registrata sul sito. */
  checkEmail(email: string): Observable<ApiResponse<boolean>> {
    return this.appService.httpGet<boolean>('User', `CheckExistingEmail/${email}`);
  }

  changeUserLanguage(): Observable<ApiResponse<unknown>> {
    return this.appService.httpGet<unknown>('User', 'ChangeUserLanguage');
  }

  getRegisterFromValues(): Observable<ApiResponse<ListObject[]>> {
    return this.appService.httpGet<ListObject[]>('User', 'GetRegisterFromValues');
  }

  /**
   * Tipo di bottone d'importazione abilitato per la pagina indicata. La risposta è testo semplice,
   * per questo la chiamata non passa da AppService, che si aspetta JSON.
   */
  updateImportButtonsState(pageWhere: string): Observable<string> {
    const url = 'User';
    const method = 'CheckEnableBTNImport/' + pageWhere;
    const httpUrl: string = this.appService.handleUrl(url, method);
    let headers: HttpHeaders = new HttpHeaders();

    if (this.bootstrap.getToken().length > 0) {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': this.bootstrap.getToken(),
      });
    }
    return this.http
      .get<string>(httpUrl, { headers, responseType: 'text' as 'json' })
      .pipe(
        catchError((error: unknown): Observable<string> => {
          // l'errore non si propaga: emette undefined e senza risposta il bottone resta nascosto
          console.error('UserService: verifica del bottone di importazione non riuscita', pageWhere, error);
          return of(undefined);
        })
      );
  }

  /**
   * Agente interno dell'utente. Nella stessa sessione e per lo stesso utente la lettura si fa una volta sola:
   * chi chiede mentre è in corso, o dopo, riceve la stessa risposta. Un errore non resta in memoria, e il
   * chiamante successivo rifà la richiesta. Si decide alla sottoscrizione.
   */
  getInternalUserAgent(idUser: number): Observable<UserAgent> {
    return defer((): Observable<UserAgent> => {
      const sessionKey: string = `${idUser}|${this.bootstrap.getToken()}`;

      if (this.internalUserAgentCache?.sessionKey !== sessionKey) {
        const agent$: Observable<UserAgent> = this.requestInternalUserAgent(idUser).pipe(
          catchError((error: unknown): Observable<never> => {
            if (this.internalUserAgentCache?.agent$ === agent$) {
              this.internalUserAgentCache = null;
            }
            return throwError((): unknown => error);
          }),
          // La richiesta prosegue anche se il primo chiamante smette di ascoltare: la risposta serve agli altri.
          shareReplay({ bufferSize: 1, refCount: false })
        );
        this.internalUserAgentCache = { sessionKey, agent$ };
      }

      return this.internalUserAgentCache.agent$;
    });
  }

  getInstanceUserAgent(idUser: number): Observable<UserAgent> {
    return this.appService.httpGet<UserAgent>(
      'User',
      `GetInstanceUserAgent/${idUser}`
    ).pipe(
      switchMap((response: ApiResponse<UserAgent> | undefined): Observable<UserAgent> =>
        response && response.Code == HTTP_STATUS_OK ? of(response.Data) : throwError((): undefined => undefined)
      )
    );
  }

  userAgentChangeRequest(
    idUser: number,
    idAgent: number,
    motivation: string
  ): Observable<ApiResponse<unknown>> {
    const parameters = {
      ID_User: idUser,
      ID_Agent: idAgent,
      motivation,
    };
    return this.appService.httpPost<unknown>(
      'User',
      'UserAgentChangeRequest',
      parameters
    ).pipe(
      // Dopo la richiesta, comunque sia andata, l'agente si rilegge dal backend alla prossima pagina.
      finalize((): void => {
        this.internalUserAgentCache = null;
      })
    );
  }
  // #endregion PublicMethods

  // #region PrivateMethods
  private requestInternalUserAgent(idUser: number): Observable<UserAgent> {
    return this.appService.httpGet<UserAgent>(
      'User',
      `GetInternalUserAgent/${idUser}`
    ).pipe(
      switchMap((response: ApiResponse<UserAgent> | undefined): Observable<UserAgent> =>
        // senza esito positivo l'errore non porta un motivo: chi chiama sa solo che l'agente non c'è
        response && response.Code == HTTP_STATUS_OK ? of(response.Data) : throwError((): undefined => undefined)
      )
    );
  }
  // #endregion PrivateMethods
}
